import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ApiService } from '../services/api.service';

interface Proforma {
  id: number;
  proformaNumber?: string;
  proformaDate?: Date;
  dueDate?: Date;
  customerId?: number;
  customerName?: string;
  customerPO?: string;
  status?: string;
  totalAmount?: number;
  amountPaid?: number;
  balance?: number;
}

@Component({
  standalone: true,
  selector: 'app-proformas',
  templateUrl: './proformas.component.html',
  styleUrl: './proformas.component.css',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    MatFormFieldModule
  ]
})
export class ProformasComponent implements OnInit {
  dataSource = new MatTableDataSource<Proforma>([]);
  displayedColumns: string[] = ['proformaNumber', 'proformaDate', 'dueDate', 'customerName', 'totalAmount', 'status'];
  loading: boolean = true;
  error: string | null = null;

  @ViewChild(MatSort) set matSort(s: MatSort) {
    if (s) { this.dataSource.sort = s; }
  }

  @ViewChild(MatPaginator) set matPaginator(p: MatPaginator) {
    if (p) { this.dataSource.paginator = p; }
  }

  constructor(
    private router: Router,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadProformas();
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  loadProformas(): void {
    this.loading = true;
    this.error = null;

    this.apiService.get<any>('proforma/all').subscribe({
      next: (data) => {
        let proformasArray: any[];
        if (Array.isArray(data)) {
          proformasArray = data;
        } else if (data && data.proformas) {
          proformasArray = data.proformas;
        } else if (data && data.data) {
          proformasArray = data.data;
        } else if (data && data.value) {
          proformasArray = data.value;
        } else {
          proformasArray = [];
        }

        const proformas = proformasArray.map(inv => ({
          id: inv.id || inv.Id,
          proformaNumber: inv.number || inv.Number,
          proformaDate: inv.date || inv.Date,
          dueDate: inv.dueDate || inv.DueDate,
          customerId: inv.customerId || inv.CustomerId,
          customerName: inv.customer?.name || inv.Customer?.Name || inv.customerName || inv.CustomerName || '',
          customerPO: inv.customerPO || inv.CustomerPO,
          status: inv.status || inv.Status,
          totalAmount: inv.totalAmount || inv.TotalAmount,
          amountPaid: inv.amountPaid || inv.AmountPaid,
          balance: (inv.totalAmount || inv.TotalAmount || 0) - (inv.amountPaid || inv.AmountPaid || 0)
        }));

        this.dataSource.data = proformas;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading proformas:', err);

        if (err.status === 0) {
          this.error = 'Cannot connect to API. Make sure the Web API is running.';
        } else if (err.status === 401) {
          this.error = 'Unauthorized. Please log out and log in again.';
        } else if (err.status === 404) {
          this.error = 'API endpoint not found.';
        } else {
          this.error = `Failed to load proformas: ${err.message || 'Unknown error'}`;
        }

        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  createNewProforma(): void {
    this.router.navigate(['/proformas/new']);
  }

  viewProforma(proforma: Proforma): void {
    this.router.navigate(['/proformas', proforma.id]);
  }

  editProforma(proforma: Proforma): void {
    this.router.navigate(['/proformas', proforma.id]);
  }
}
